// GraphQL field declaration for the activeOrders query, passed into useDataModel's `dataModel`
// option — mirrors purchaseHistoryDataModel.ts's structure. Originally grounded only against the
// Jira OOP-3943 sample; the selection below is now confirmed against the real staging schema
// (2026-08-12), which validated the `activeOrders` root field, `HeaderExtras`/`ActiveOrdersRequest!`,
// and every field still selected here.
//
// Seven fields were once rejected as FieldUndefined by the staging schema (2026-08-12) and left
// out of the selection: `ActiveDistribution.workOrderNumber`/`trackingNumber`,
// `ActiveOrdersLocation.addressLine2`, `ActiveOrdersInstructions.additionalInstructions`,
// `ActiveOrdersLineItem.imageUrl`, and both `ActiveOrdersRecipient` fields (`name`, `phoneNumber`).
// order-management-svc's schema.graphqls on `develop` (checked 2026-09-30) declares all of them —
// plus `ActiveOrdersLocation.storePhoneNumber` and `ActiveOrdersLineItem.lineId` — so they are
// selected again below. If a deployed schema that lags `develop` rejects any of them again, the
// WHOLE query fails (see the NOTE below): remove the offending field here only; types.ts, the
// adapters and mock-data keep them optional/nullable.
//
// GraphQL has no way to make a selection "optional" — a selected field not in the schema fails
// validation for the ENTIRE document, and @skip/@include don't help (they still require the field
// to exist). "Tolerate it when present" can only live in the TypeScript types/adapters, never in
// the query.
//
// orderOrigin/storeNumber/transactionId/registerNumber/salesDate were a SEPARATE, LATER addition
// (OOP-4197, so an active-orders row would carry enough identifying data to build an orderDetails
// request — see src/data/adapters/fulfillmentDrawer.ts's buildActiveOrderDetailsParams). All five
// were REJECTED as FieldUndefined by the real stage schema (2026-09-17) — `ActiveDistribution` has
// none of them, confirmed by a live stage error report — and REMOVED from the selection below
// (exactly the "whole activeOrders query fails for every consumer" failure mode this comment
// previously warned about, and it happened). Same selection-set-only-removal treatment as the
// seven fields above: `RawActiveDistribution` keeps all five as optional/nullable, and
// mock-data/active-orders-response.ts still supplies them for buildActiveOrderDetailsParams' own
// unit tests — but a REAL response will never populate them, so that function's 'inStore' branch
// is effectively unreachable in production until a schema exposing these (or equivalents) is
// targeted; `orderOrigin` being permanently absent also means the 'online' variant always falls
// through to its orderNumber-prefix regex, never the orderOrigin check. See ADR-003's OOP-4197
// addendum.
//
// NOTE: a failing selection set takes the Deliveries/Pickups toggle down with it —
// ActiveOrdersTable returns [] for fulfillmentFilterOptions on any non-success status, and the
// presentational shell gates the toggle on that array being non-empty. The toggle vanishing is a
// symptom of query failure, not a toggle bug.
//
// Two structural differences from purchaseHistory, both confirmed by the sample: no top-level
// identity argument (no `userId` alongside `request`) — `customerAccountId`/`purchaserId` both
// live inside `request`; and `filters` entries are singular `{name, value}`, not `{name, values: []}`.
//
// THE REQUEST CONTRACT (confirmed 2026-08-12). `ActiveOrdersRequest` takes exactly:
// customerAccountId, purchaserId, pageSize, pageNumber, filters. Notably:
//
// * No startDate/endDate fields. This API expresses a date window only through the
//   `{name: 'distributionDate'}` filter sentinel — one of 'ALL' | 'Today' | 'Tomorrow' |
//   'Next 7 Days' (exact casing/spacing; see ActiveOrdersDateSentinel below). An earlier revision
//   sent startDate/endDate and dropped the sentinel; both were wrong, now reversed. There is
//   consequently no way to request an arbitrary date range, so ActiveOrdersTable hides
//   DateRangeFilter's custom-range inputs (allowCustomRange={false}) — see ADR-003.
// * `{name: 'distributionType'}` takes 'ALL' | 'DELIVERY' | 'PICKUP' (uppercase).
//
// searchValue/sortBy/sortOrder are NOT in that contract and are no longer sent (2026-09-21):
// order-management-svc's schema.graphqls defines `ActiveOrdersRequest` with exactly
// customerAccountId/purchaserId/pageSize/pageNumber/filters — confirmed against the OMS source
// directly, not staging introspection — so these three guessed names are absent from the input
// type, not just unconfirmed. As predicted above, an unknown input-object field is a
// variable-coercion error that fails the whole request; ActiveOrdersTable.tsx no longer wires the
// search box or sortable column headers to this query as a result (see its own comment). The same
// never-validated caveat still applies to the `filters` entry shape and to HeaderExtras'
// tmxProfileId/channelId (also unread by the current OMS resolver). `ActiveOrdersFilter` never
// appears in the emitted document at all (nested inside the `$request` variable), so a wrong name
// there is also a coercion error.
//
// A top-level `userId` argument IS required here, despite order-management-svc's checked-in
// schema.graphqls declaring `activeOrders(headers: HeaderExtras, request: ActiveOrdersRequest!):
// ActiveOrdersResponse!` with no such argument (confirmed against `develop`, 2026-09-22 — see
// concerns.instructions.md C6). A real request without `userId` fails; a real, working sample
// request has it set alongside `headers`/`request`, equal to `request.purchaserId`. This is a
// schema/actual-behavior mismatch (the deployed resolver needs an argument its own SDL doesn't
// declare) — do not "fix" this back out based on the SDL file alone.
import { params, shape, arrayOf, string, number, bool, id, customType } from '@thd-nucleus/data-sources';

export interface ActiveOrdersRequestFilter {
  name: string;
  value: string;
}

/**
 * The only values `{name: 'distributionDate'}` accepts — confirmed against the real request
 * contract (2026-08-12). Exact casing/spacing: title case with spaces ('Next 7 Days'), NOT this
 * repo's internal camelCase preset slugs ('next7Days'). These happen to match
 * `buildActiveOrdersDateRangePresets()`'s `label`s, but are mapped explicitly via
 * ACTIVE_ORDERS_DATE_SENTINEL below rather than derived from labels, so relabeling a preset for UX
 * reasons can't silently change what goes on the wire.
 */
export type ActiveOrdersDateSentinel = 'ALL' | 'Today' | 'Tomorrow' | 'Next 7 Days';

/** The only values `{name: 'distributionType'}` accepts — confirmed against the same contract. */
export type ActiveOrdersDistributionType = 'ALL' | 'DELIVERY' | 'PICKUP';

/**
 * Maps this repo's internal date-range preset values to the API's `distributionDate` sentinels.
 * There is no `'custom'` entry — the API has no startDate/endDate fields on ActiveOrdersRequest,
 * so ActiveOrdersTable does not offer a custom range. See ActiveOrdersTable.tsx.
 */
export const ACTIVE_ORDERS_DATE_SENTINEL: Record<string, ActiveOrdersDateSentinel> = {
  all: 'ALL',
  today: 'Today',
  tomorrow: 'Tomorrow',
  next7Days: 'Next 7 Days'
};

export interface ActiveOrdersQueryVariables {
  headers?: {
    tmxProfileId?: string;
    channelId?: string;
  };
  request: {
    customerAccountId: string;
    purchaserId: string;
    pageSize: number;
    pageNumber: number;
    filters?: ActiveOrdersRequestFilter[];
  };
  userId: string;
}

export const activeOrdersDataModel = {
  activeOrders: params({
    headers: customType('HeaderExtras').shape({ tmxProfileId: string(), channelId: string() }),
    request: customType('ActiveOrdersRequest!').shape({
      customerAccountId: id().isRequired(),
      purchaserId: id().isRequired(),
      pageSize: number(),
      pageNumber: number(),
      filters: arrayOf(customType('ActiveOrdersFilter').shape({ name: string(), value: string() }))
    }),
    userId: id().isRequired()
  }).shape({
    customerAccountId: id(),
    userId: id(),
    totalCardCount: number(),
    totalDeliveryCount: number(),
    totalPickupCount: number(),
    activeDistributions: arrayOf(
      shape({
        distributionType: string(),
        orderNumber: string(),
        projectName: string(),
        poJobName: string(),
        status: string(),
        distributionDate: string(),
        distributionTime: string(),
        workOrderNumber: string(),
        trackingNumber: string(),
        recipient: shape({
          name: string(),
          phoneNumber: string()
        }),
        location: shape({
          type: string(),
          addressLine1: string(),
          addressLine2: string(),
          city: string(),
          state: string(),
          zipCode: string(),
          storeId: string(),
          storePhoneNumber: string(),
          description: string()
        }),
        instructions: shape({
          securityCode: string(),
          signatureRequired: bool(),
          driverContact: string(),
          additionalInstructions: string()
        }),
        lineItems: arrayOf(
          shape({
            lineId: string(),
            name: string(),
            imageUrl: string(),
            quantity: number(),
            unitPrice: number({ float: true }),
            totalPrice: number({ float: true })
          })
        )
      })
    )
  })
};
